/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@/components/ui/dialog';
import {
  AgencyPlan,
  getAgencyPlanColor,
  getAgencyPlanLabel,
  getAgencyPlanOptions
} from '@/types/enums';
import {
  CaretDown,
  Info,
  X,
  Users,
  ClipboardText,
  CalendarCheck,
  Suitcase,
  SignIn
} from '@phosphor-icons/react';
import { Box, Button, Flex, Menu, Stack, Switch, Text } from '@chakra-ui/react';
import {
  apiAdminGetUserProfile,
  apiAdminUpdateAgencyPlan,
  apiAdminUpdateAgencyCustomFeature,
  apiAdminGetAccountToken,
  AdminGetAccountTokenResponse
} from '@/api/services';
import { fetchAPI } from '@/api/fetch';
import { LoadingSkeleton } from '@/components/layout/LoadingSkeleton';
import useAuth from '@/store/auth';
import { AgencyCustomFeature } from '@/types/AgencyCustomFeature';
import { AgencyFeature } from '@/types/enums/AgencyFeature';

interface UserDetailsDialogProps {
  accountId: string;
  open: boolean;
  onClose: () => void;
  onUpdate: (account: Account) => void;
}

export default function UserDetailsDialog({
  accountId,
  open = false,
  onClose,
  onUpdate
}: UserDetailsDialogProps) {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [account, setAccount] = useState<Account | null>();
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [agencyPlan, setAgencyPlan] = useState<{ value: AgencyPlan } | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [togglingFeature, setTogglingFeature] = useState<AgencyFeature | null>(null);

  useEffect(() => {
    if (!accountId) return;
    if (open) {
      fetchAccountDetails(accountId);
    }
  }, [open]);

  const fetchAccountDetails = useCallback(async (accountId: string) => {
    resetState();
    setIsLoading(true);

    try {
      const response = await fetchAPI<Account>({
        request: () => apiAdminGetUserProfile(accountId)
      });
      if (response.success && response.data) {
        const acc = response.data;
        setAccount(acc);
        setAgencyPlan({ value: acc.agency.plan });
      } else {
        console.error('Failed to fetch account details:', response.message);
        setFetchError('Failed to load account details. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching account details:', error);
      setFetchError('Failed to load account details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetState = useCallback(() => {
    setAccount(null);
    setFetchError(null);
    setIsLoading(false);
    setIsSubmitting(false);
    setSubmitError(null);
    setAgencyPlan(null);
  }, []);

  const handleUpdate = useCallback(
    async (plan: AgencyPlan) => {
      if (!account) return;

      setSubmitError(null);
      setIsSubmitting(true);

      try {
        const response = await fetchAPI<void>({
          request: () =>
            apiAdminUpdateAgencyPlan({
              agency_id: account.agency.agency_id,
              plan: plan
            })
        });
        if (response.success) {
          const updatedAccount = {
            ...account,
            agency: {
              ...account.agency,
              plan: plan,
              trial_start_date:
                plan === AgencyPlan.ONE_MONTH_TRIAL ? Math.floor(Date.now() / 1000) : undefined,
              trial_expiration_date:
                plan === AgencyPlan.ONE_MONTH_TRIAL
                  ? Math.floor(Date.now() / 1000) + 30 * 24 * 60 * 60
                  : undefined
            }
          };
          setAccount(updatedAccount);
          onUpdate(updatedAccount);
        } else {
          console.error('Failed to update agency plan:', response.message);
          setSubmitError('Failed to update agency plan. Please try again.');
        }
      } catch (error) {
        console.error('Error updating agency plan:', error);
        setSubmitError('An unexpected error occurred. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [account, onClose, onUpdate]
  );

  const handlePlanChange = (newPlan: AgencyPlan) => {
    if (!account) return;

    if (newPlan === account.agency.plan) return;
    setAgencyPlan({ value: newPlan });
  };

  const handleToggleCustomFeature = useCallback(
    async (featureKey: AgencyFeature, isEnabled: boolean) => {
      if (!account) return;

      setTogglingFeature(featureKey);

      const prevFeatures = account.agency.custom_features;
      const updatedFeatures = prevFeatures.some(
        (f: AgencyCustomFeature) => f.feature_key === featureKey
      )
        ? prevFeatures.map((f: AgencyCustomFeature) =>
            f.feature_key === featureKey ? { ...f, is_enabled: isEnabled } : f
          )
        : [
            ...prevFeatures,
            {
              feature_id: '',
              agency_id: account.agency.agency_id,
              feature_key: featureKey,
              is_enabled: isEnabled
            } as AgencyCustomFeature
          ];

      const updatedAccount = {
        ...account,
        agency: { ...account.agency, custom_features: updatedFeatures }
      };
      setAccount(updatedAccount);

      try {
        const response = await fetchAPI<void>({
          request: () =>
            apiAdminUpdateAgencyCustomFeature({
              agency_id: account.agency.agency_id,
              feature_key: featureKey,
              is_enabled: isEnabled
            })
        });
        if (response.success) {
          onUpdate(updatedAccount);
        } else {
          console.error('Failed to update custom feature:', response.message);
          setAccount({ ...account, agency: { ...account.agency, custom_features: prevFeatures } });
        }
      } catch (error) {
        console.error('Error updating custom feature:', error);
        setAccount({ ...account, agency: { ...account.agency, custom_features: prevFeatures } });
      } finally {
        setTogglingFeature(null);
      }
    },
    [account, onUpdate]
  );

  const handleLoginAsUser = useCallback(async () => {
    if (!account) return;

    setLoginError(null);
    setIsLoggingIn(true);

    try {
      const response = await fetchAPI<AdminGetAccountTokenResponse>({
        request: () => apiAdminGetAccountToken({ account_id: account.account_id })
      });

      if (response.success && response.data) {
        // Login with the user's credentials
        login(response.data.account, response.data.access_token, response.data.refresh_token);

        // Navigate to the main app dashboard (not admin area)
        navigate('/');
      } else {
        console.error('Failed to get account token:', response.message);
        setLoginError('Failed to login as user. Please try again.');
      }
    } catch (error) {
      console.error('Error getting account token:', error);
      setLoginError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  }, [account, login, navigate]);

  return (
    <DialogRoot
      size="xl"
      open={open}
      onOpenChange={({ open }) => {
        if (!open) {
          onClose();
        }
      }}
      closeOnInteractOutside={false}
      placement="center"
      motionPreset="slide-in-bottom"
      unmountOnExit
    >
      <DialogContent borderRadius="12px" maxW="600px">
        <DialogHeader borderBottom="1px solid" borderColor="neutrals.200" pb="16px">
          <DialogTitle fontSize="24px" fontWeight="semibold">
            User Details
          </DialogTitle>
        </DialogHeader>
        <DialogBody py="24px" maxH="70vh" overflowY="auto">
          {isLoading ? (
            <LoadingSkeleton count={3} />
          ) : fetchError || !account || !agencyPlan ? (
            <Stack gap="32px" align="center" justify="center" minH="200px">
              <Text fontSize="16px" color="error.700" textAlign="center">
                {fetchError}
              </Text>
              <Button
                onClick={() => {
                  fetchAccountDetails(accountId);
                }}
                variant="outline"
                borderColor="primary.900"
                color="primary.900"
                borderRadius="8px"
                fontSize="16px"
                p="12px 24px"
                w="fit-content"
              >
                Retry
              </Button>
            </Stack>
          ) : (
            <Stack gap="6">
              <Box>
                <Text fontSize="14px" color="neutrals.600" mb="4px">
                  Account Information
                </Text>
                <Box p="16px" bg="neutrals.50" borderRadius="8px">
                  <Stack gap="3">
                    <Flex justify="space-between" align="center">
                      <Text fontSize="14px" color="neutrals.700">
                        Account ID
                      </Text>
                      <Text fontSize="14px" fontWeight="medium">
                        {account.account_id}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="14px" color="neutrals.700">
                        Name
                      </Text>
                      <Text fontSize="14px" fontWeight="medium">
                        {account.name}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="14px" color="neutrals.700">
                        Email
                      </Text>
                      <Text fontSize="14px" fontWeight="medium">
                        {account.email}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="14px" color="neutrals.700">
                        Last Active
                      </Text>
                      <Stack gap="1" align="flex-end">
                        <Text fontSize="14px" fontWeight="medium">
                          {new Date(account.last_active * 1000).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Text>
                        <Text fontSize="12px" color="neutrals.500">
                          {(() => {
                            const lastActiveDate = new Date(account.last_active * 1000);
                            const today = new Date();
                            const diffTime = Math.abs(today.getTime() - lastActiveDate.getTime());
                            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                            return diffDays === 0
                              ? 'Today'
                              : `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
                          })()}
                        </Text>
                      </Stack>
                    </Flex>
                  </Stack>
                </Box>
              </Box>

              <Box>
                <Text fontSize="14px" color="neutrals.600" mb="4px">
                  Agency Information
                </Text>
                <Box p="16px" bg="neutrals.50" borderRadius="8px">
                  <Stack gap="3">
                    <Flex justify="space-between" align="center">
                      <Text fontSize="14px" color="neutrals.700">
                        Agency ID
                      </Text>
                      <Text fontSize="14px" fontWeight="medium">
                        {account.agency.agency_id}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="14px" color="neutrals.700">
                        Agency Name
                      </Text>
                      <Text fontSize="14px" fontWeight="medium">
                        {account.agency.name}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="14px" color="neutrals.700">
                        Current Plan
                      </Text>
                      <Box
                        px="12px"
                        py="4px"
                        borderRadius="8px"
                        border="1px solid"
                        borderColor={getAgencyPlanColor(account.agency.plan)}
                        color={getAgencyPlanColor(account.agency.plan)}
                        fontSize="14px"
                        fontWeight="medium"
                      >
                        {getAgencyPlanLabel(account.agency.plan)}
                      </Box>
                    </Flex>
                    {account.agency.plan === AgencyPlan.ONE_MONTH_TRIAL &&
                      account.agency.trial_start_date &&
                      account.agency.trial_expiration_date && (
                        <>
                          <Flex justify="space-between" align="center">
                            <Text fontSize="14px" color="neutrals.700">
                              Trial Start Date
                            </Text>
                            <Text fontSize="14px" fontWeight="medium">
                              {new Date(account.agency.trial_start_date * 1000).toLocaleDateString(
                                'en-US',
                                {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }
                              )}
                            </Text>
                          </Flex>
                          <Flex justify="space-between" align="center">
                            <Text fontSize="14px" color="neutrals.700">
                              Trial Expiration Date
                            </Text>
                            <Text fontSize="14px" fontWeight="medium">
                              {new Date(
                                account.agency.trial_expiration_date * 1000
                              ).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </Text>
                          </Flex>
                        </>
                      )}
                  </Stack>
                </Box>
              </Box>

              <Box>
                <Text fontSize="14px" color="neutrals.600" mb="4px">
                  Custom Features
                </Text>
                <Box p="16px" bg="neutrals.50" borderRadius="8px">
                  <Stack gap="3">
                    {Object.values(AgencyFeature).map((featureKey) => {
                      const feature = account.agency.custom_features.find(
                        (f: AgencyCustomFeature) => f.feature_key === featureKey
                      );
                      const isEnabled = feature?.is_enabled ?? false;
                      const isToggling = togglingFeature === featureKey;
                      return (
                        <Flex key={featureKey} justify="space-between" align="center">
                          <Text fontSize="14px" color="neutrals.700" textTransform="capitalize">
                            {featureKey.replace(/_/g, ' ')}
                          </Text>
                          <Switch.Root
                            checked={isEnabled}
                            onCheckedChange={({ checked }) =>
                              handleToggleCustomFeature(featureKey, checked)
                            }
                            disabled={isToggling}
                            size="sm"
                          >
                            <Switch.HiddenInput />
                            <Switch.Control>
                              <Switch.Thumb />
                            </Switch.Control>
                          </Switch.Root>
                        </Flex>
                      );
                    })}
                  </Stack>
                </Box>
              </Box>

              <Box>
                <Text fontSize="14px" color="neutrals.600" mb="4px">
                  Statistics
                </Text>
                <Box p="16px" bg="neutrals.50" borderRadius="12px">
                  <Stack gap="4">
                    <Flex gap="4" wrap="wrap">
                      <Box flex="1" minW="140px" borderRadius="12px">
                        <Flex
                          align="center"
                          gap="3"
                          p="12px"
                          bg="white"
                          borderRadius="8px"
                          border="1px solid"
                          borderColor="neutrals.200"
                        >
                          <Box p="8px" borderRadius="6px">
                            <Users size={20} weight="duotone" />
                          </Box>
                          <Stack gap="0" flex="1">
                            <Text fontSize="18px" fontWeight="bold" color="neutrals.900">
                              {account.statistics.total_candidates.toLocaleString()}
                            </Text>
                            <Text fontSize="12px" color="neutrals.600">
                              Candidates
                            </Text>
                          </Stack>
                        </Flex>
                      </Box>
                      <Box flex="1" minW="140px" borderRadius="12px">
                        <Flex
                          align="center"
                          gap="3"
                          p="12px"
                          bg="white"
                          borderRadius="8px"
                          border="1px solid"
                          borderColor="neutrals.200"
                        >
                          <Box p="8px" borderRadius="6px" bg="success.50">
                            <Suitcase
                              size={20}
                              weight="duotone"
                              color="var(--chakra-colors-success-600)"
                            />
                          </Box>
                          <Stack gap="0" flex="1" borderRadius="12px">
                            <Text fontSize="18px" fontWeight="bold" color="neutrals.900">
                              {account.statistics.total_jobs.toLocaleString()}
                            </Text>
                            <Text fontSize="12px" color="neutrals.600">
                              Jobs
                            </Text>
                          </Stack>
                        </Flex>
                      </Box>
                    </Flex>
                    <Flex gap="4" wrap="wrap">
                      <Box flex="1" minW="140px" borderRadius="12px">
                        <Flex
                          align="center"
                          gap="3"
                          p="12px"
                          bg="white"
                          borderRadius="8px"
                          border="1px solid"
                          borderColor="neutrals.200"
                        >
                          <Box p="8px" borderRadius="6px" bg="warning.50">
                            <ClipboardText
                              size={20}
                              weight="duotone"
                              color="var(--chakra-colors-warning-600)"
                            />
                          </Box>
                          <Stack gap="0" flex="1">
                            <Text fontSize="18px" fontWeight="bold" color="neutrals.900">
                              {account.statistics.total_screenings.toLocaleString()}
                            </Text>
                            <Text fontSize="12px" color="neutrals.600">
                              Screenings
                            </Text>
                          </Stack>
                        </Flex>
                      </Box>
                      <Box flex="1" minW="140px">
                        <Flex
                          align="center"
                          gap="3"
                          p="12px"
                          bg="white"
                          borderRadius="8px"
                          border="1px solid"
                          borderColor="neutrals.200"
                        >
                          <Box p="8px" borderRadius="6px" bg="info.50">
                            <CalendarCheck
                              size={20}
                              weight="duotone"
                              color="var(--chakra-colors-info-600)"
                            />
                          </Box>
                          <Stack gap="0" flex="1">
                            <Text fontSize="18px" fontWeight="bold" color="neutrals.900">
                              {account.statistics.total_meetings.toLocaleString()}
                            </Text>
                            <Text fontSize="12px" color="neutrals.600">
                              Meetings
                            </Text>
                          </Stack>
                        </Flex>
                      </Box>
                    </Flex>
                  </Stack>
                </Box>
              </Box>

              <Box>
                <Flex align="center" gap="2" mb="8px">
                  <Text fontSize="14px" color="neutrals.600">
                    Update Plan
                  </Text>
                  <Box
                    title="Changing the plan will affect the agency's features and capabilities"
                    cursor="pointer"
                  >
                    <Info size={16} color="var(--chakra-colors-neutrals-600)" />
                  </Box>
                </Flex>
                <Flex justify="space-between" gap="16px">
                  <Box flex="1">
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          width="100%"
                          borderRadius="8px"
                          borderColor="neutrals.300"
                          bg="background.secondary"
                          color="neutrals.700"
                          disabled={isSubmitting}
                          _disabled={{
                            bg: 'neutrals.300'
                          }}
                          _hover={{
                            borderColor: 'neutrals.400',
                            opacity: 0.8
                          }}
                        >
                          <Flex
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                            gap="8"
                          >
                            <Box fontSize="16px">{getAgencyPlanLabel(agencyPlan.value)}</Box>
                            <Box as="span" ml="auto" mr="0">
                              <CaretDown size={16} />
                            </Box>
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Menu.Positioner>
                        <Menu.Content minW="10rem" zIndex={1500} borderRadius="8px" boxShadow="lg">
                          <Menu.RadioItemGroup
                            value={agencyPlan.value}
                            onValueChange={(v) => handlePlanChange(v.value as AgencyPlan)}
                          >
                            {getAgencyPlanOptions().map((item) => (
                              <Menu.RadioItem
                                key={item.value}
                                value={item.value}
                                fontSize="16px"
                                _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                                color="neutrals.700"
                              >
                                {item.label}
                              </Menu.RadioItem>
                            ))}
                          </Menu.RadioItemGroup>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Menu.Root>
                  </Box>
                  <Button
                    type="submit"
                    w="160px"
                    background="primary.900"
                    color="neutrals.0"
                    borderRadius="8px"
                    fontSize="16px"
                    aria-label="Submit"
                    loading={isSubmitting}
                    onClick={() => handleUpdate(agencyPlan.value)}
                    disabled={
                      isLoading || !!fetchError || account?.agency.plan === agencyPlan.value
                    }
                    _hover={{
                      bg: 'primary.800'
                    }}
                  >
                    Update Plan
                  </Button>
                </Flex>
                {submitError && (
                  <Box p="12px" bg="error.50" borderRadius="8px">
                    <Text color="error.500" fontSize="14px">
                      {submitError}
                    </Text>
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </DialogBody>
        <DialogFooter borderTop="1px solid" borderColor="neutrals.200" pt="16px">
          <Flex justify="space-between" width="100%">
            <Button
              w="180px"
              background="primary.900"
              color="neutrals.0"
              borderRadius="8px"
              fontSize="16px"
              aria-label="Login as User"
              disabled={isSubmitting || isLoggingIn || !account}
              loading={isLoggingIn}
              onClick={handleLoginAsUser}
              _hover={{
                bg: 'primary.800'
              }}
            >
              <Flex align="center" gap="8px">
                <SignIn size={20} weight="bold" />
                <Text>Login as User</Text>
              </Flex>
            </Button>
            <DialogActionTrigger asChild>
              <Button
                w="160px"
                borderColor="primary.900"
                background="background.secondary"
                color="primary.900"
                borderRadius="8px"
                fontSize="16px"
                aria-label="Cancel"
                disabled={isSubmitting || isLoggingIn}
                _hover={{
                  bg: 'neutrals.100'
                }}
              >
                Close
              </Button>
            </DialogActionTrigger>
          </Flex>
          {loginError && (
            <Box p="12px" bg="error.50" borderRadius="8px" mt="12px">
              <Text color="error.500" fontSize="14px">
                {loginError}
              </Text>
            </Box>
          )}
        </DialogFooter>
        <DialogCloseTrigger m="12px" disabled={isSubmitting || isLoggingIn}>
          <X size={32} weight="bold" aria-label="Close dialog" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
